import { ReactNode, useCallback, useRef, ChangeEvent, MouseEvent } from 'react';
import { useFormContext, RegisterOptions } from 'react-hook-form';

import PillInput from './pill-input';
import { pushDataTrackerEvent } from 'approot/shared/data-tracker/data-tracker-push';
import ErrorMessageStyled from 'approot/shared/error-message/error-message.style';
import { InlineSvgIds } from 'approot/shared/inline-svg/inline-svg';
import { PillsInputs } from './pill.style';
import { DataTrackerEventData } from 'approot/shared/data-tracker/data-tracker.constants';
import { ScreenReaderOnly } from 'approot/shared/styles/global.styles';
import { hasSvgMapping } from '../inline-svg/has-no-svg-mapping';

export type Option = {
  value: string;
  label: ReactNode;
  isDisabled?: boolean;
};

export type SingleValue = string;
export type MultiSelectValue = string[];

type CommonProps = {
  fieldName: string;
  className?: string;
  serverMessage?: ReactNode;
  legendLabel?: ReactNode;
  options: Option[];
  loading?: boolean;
  emptyMessage?: string;
  isIconPicker?: boolean;
  error?: ReactNode;
  hasBorder?: boolean;
  hasIcon?: boolean;
  centered?: boolean;
  validation?: RegisterOptions;
  isMultiSelect?: boolean;
  isSmall?: boolean;
  required?: boolean;
  dataEvent?: {
    eventCategory: string;
    eventAction: string;
    eventLabel: (value: string) => DataTrackerEventData;
  };
};

type MultiSelectProps<Value extends MultiSelectValue = MultiSelectValue> = {
  isMultiSelect: true;
  value?: Value;
  initialValue?: Value;
  preventEmptySelection?: boolean;
  onChange?: (value: Value, shouldValidate?: boolean) => void;
  onBeforeChange?: (fieldName: string, value: string) => void;
  getSelectedPills?: (
    previousSelections: Value,
    currentSelection: Value[number]
  ) => Value;
  // Existence check assumes single selection
  checkSelectedExists?: undefined;
};

type SingleProps<Value extends SingleValue = SingleValue> = {
  isMultiSelect?: false;
  value?: Value;
  initialValue?: Value;
  preventEmptySelection?: undefined;
  onChange?: (value: Value, shouldValidate?: boolean) => void;
  onBeforeChange?: (fieldName: string, value: string) => void;
  getSelectedPills?: undefined;
};

export type SinglePillsPickerProps<Value extends SingleValue = SingleValue> =
  CommonProps & SingleProps<Value>;

export type MultiSelectPillsPickerProps<
  Value extends MultiSelectValue = MultiSelectValue,
> = CommonProps & MultiSelectProps<Value>;

function PillsPicker<Value extends MultiSelectValue>(
  props: MultiSelectPillsPickerProps<Value>
): JSX.Element;
function PillsPicker<Value extends SingleValue>(
  props: SinglePillsPickerProps<Value>
): JSX.Element;
function PillsPicker(
  props: SinglePillsPickerProps | MultiSelectPillsPickerProps
) {
  const {
    fieldName,
    className,
    isIconPicker,
    serverMessage,
    legendLabel,
    options,
    loading,
    emptyMessage,
    isMultiSelect,
    error,
    hasBorder,
    hasIcon,
    centered,
    validation,
    required,
    onBeforeChange,
    initialValue,
    isSmall,
    dataEvent,
  } = props;

  // Note:
  // We are not destructuring props specific to multiSelect vs single
  // so that Typescript can infer the correct value type.
  // That means we need to destructure functions inside of conditionals
  //  before using them to ensure they do not depend on `props` as a whole.
  // https://github.com/facebook/react/issues/14920#issuecomment-467494468

  const { getValues, setValue } = useFormContext();

  const currentSelection = useRef<string | string[] | undefined>(initialValue);

  const handleOnClick = useCallback(
    (event: MouseEvent) => {
      const { preventEmptySelection, isMultiSelect } = props;
      const values = getValues(fieldName);
      if (
        isMultiSelect &&
        preventEmptySelection &&
        Array.isArray(values) &&
        values.length === 0
      ) {
        event.preventDefault();
        const target = event.target as HTMLInputElement;
        setValue(fieldName, [target.value]);
        return false;
      }

      // if this is single select and the same value has been clicked do nothing
      if (!isMultiSelect && props.value) {
        const target = event.target as HTMLInputElement;
        if (target.value === props.value) {
          return false;
        }
      }

      if (onBeforeChange) {
        event.preventDefault();
        const target = event.target as HTMLInputElement;
        onBeforeChange(fieldName, target.value);
      }
    },
    [onBeforeChange, fieldName, getValues, setValue, props]
  );

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      if (onBeforeChange) {
        event.preventDefault();
        return false;
      }

      if (dataEvent) {
        const value = event.target.value;
        const values = getValues(fieldName);
        pushDataTrackerEvent(
          dataEvent.eventCategory,
          `${values.includes(value) ? 'add' : 'remove'}-${
            dataEvent.eventAction
          }`,
          typeof dataEvent.eventLabel === 'string'
            ? {
                target_type: dataEvent.eventLabel,
                target_identifier: value,
              }
            : dataEvent.eventLabel(value)
        );
      }

      const { preventEmptySelection, isMultiSelect } = props;
      const values = getValues(fieldName);
      if (
        isMultiSelect &&
        preventEmptySelection &&
        Array.isArray(values) &&
        values.length === 0
      ) {
        setValue(fieldName, [event.target.value]);
        return false;
      }

      if (
        isMultiSelect &&
        props.getSelectedPills &&
        Array.isArray(currentSelection.current)
      ) {
        const newSelections = props.getSelectedPills(
          currentSelection.current,
          event.target.value
        );

        currentSelection.current = newSelections;
        setValue(fieldName, newSelections);

        if (props.onChange) {
          props.onChange(newSelections);
        }
      } else {
        if (props.onChange) {
          props.onChange(getValues(fieldName));
        }
      }
    },
    [fieldName, getValues, props, setValue, onBeforeChange, dataEvent]
  );

  const isChecked = useCallback(
    (option: string) => {
      if (props.isMultiSelect) {
        return !!props.value && props.value.includes(option);
      } else {
        return props.value === option;
      }
    },
    [props.value, props.isMultiSelect]
  );

  const hasError = !!(error || serverMessage);

  return (
    <fieldset>
      {legendLabel && (
        <ScreenReaderOnly as="legend">{legendLabel}</ScreenReaderOnly>
      )}
      {options && options.length > 0 ? (
        <PillsInputs
          className={className}
          $centered={centered}
          data-selector={`pills-${fieldName}`}
          role={isMultiSelect ? 'group' : 'radiogroup'}
          tabIndex={-1}
          id={`pills-${fieldName}`}
        >
          {options.map((option, i) => (
            <PillInput
              disabled={option.isDisabled}
              checked={isChecked(option.value)}
              hasBorder={hasBorder}
              isSmall={isSmall}
              iconId={
                hasIcon && hasSvgMapping(option.value)
                  ? (option.value as InlineSvgIds)
                  : undefined
              }
              fieldName={fieldName}
              isMultiSelect={isMultiSelect}
              key={`${fieldName}_${option.value}`}
              centered={centered}
              id={`${fieldName}_${option.value}`}
              value={option.value}
              onClick={handleOnClick}
              onChange={handleChange}
              isIconPicker={isIconPicker}
              validation={validation}
              aria-required={required}
              hasError={hasError}
            >
              {option.label}
            </PillInput>
          ))}
        </PillsInputs>
      ) : loading ? (
        <div>
          <p>Loading...</p>
        </div>
      ) : (
        <div>
          <p>{emptyMessage}</p>
        </div>
      )}
      {hasError && (
        <ErrorMessageStyled
          data-selector={`${fieldName}-error-message`}
          $marginTop={0}
          id={`${fieldName}-error-message`}
        >
          {error || serverMessage}
        </ErrorMessageStyled>
      )}
    </fieldset>
  );
}

export default PillsPicker;
